/*
 * Thin singleton wrapper around @microsoft/signalr's HubConnection.
 *
 * Concept C.2: one long-lived SignalR connection per session, three logical
 * channels (`values`, `alarms`, `commands`). This module owns the connection
 * and multiplexes fan-out to whichever module wants to listen. The Signal-Store
 * (signalStore.ts) is the intended API for the `values` channel; nothing else
 * in the app should touch the raw HubConnection.
 *
 * Concept C.3: same-origin cookie auth. The BFF put an HttpOnly session cookie
 * on the browser at login; the browser attaches it to the SignalR websocket
 * handshake automatically. If the cookie is missing/expired, the hub 401s and
 * the caller falls through to the login redirect (AuthGuard handles that).
 *
 * Connection state is exposed as a Signal, so both the top-bar
 * ConnectionIndicator and the freshness heuristic can react without wiring
 * event listeners themselves.
 */

import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
  type IRetryPolicy,
  type RetryContext,
} from '@microsoft/signalr';
import { signal, type Signal } from '@preact/signals';
import { redirectToLogin } from '../auth/session';
import type { AlarmEvent, CommandEvent, NotificationDto } from '../api/types';

/**
 * Custom retry policy that NEVER gives up. SignalR's default array policy
 * (0, 2, 10, 30 s) stops after four attempts — that's the "UI frozen,
 * F5 fixes it" bug: after a ~42 s network hiccup (VPN blip, laptop
 * suspend, API redeploy) the hub is dead and no live values flow. This
 * policy retries with capped exponential backoff forever, so the app
 * self-heals whenever the network comes back.
 */
const InfiniteRetryPolicy: IRetryPolicy = {
  nextRetryDelayInMilliseconds(ctx: RetryContext): number | null {
    const attempts = ctx.previousRetryCount;
    // Same first four steps as the default so nothing regresses for
    // short outages; after that grow to a 30 s ceiling and stay there.
    if (attempts === 0) return 0;
    if (attempts === 1) return 2_000;
    if (attempts === 2) return 10_000;
    return 30_000;
  },
};

/** Fired whenever the connection transitions to fully-connected — either
 *  the first `start()` or a successful auto-reconnect. Bootstrappers use
 *  it to re-hydrate their in-memory state that may have missed pushes
 *  during the outage. */
type ReconnectHandler = () => void;
const reconnectHandlers = new Set<ReconnectHandler>();

/** UI-facing state. Slightly richer than SignalR's own state so we can tell
 *  "first connect" apart from "reconnecting after drop". */
export type ConnectionState = 'connecting' | 'connected' | 'disconnected' | 'reconnecting';

/** Wire shape emitted by the server on the `values` event. Field names are
 *  short on purpose — this batch is the hot path. `p` = plant id, `u` = updates,
 *  `id` = leaf id, `v` = value, `q` = quality, `t` = server timestamp. */
export interface WireValuesBatch {
  p: string;
  u: Array<{ id: string; v: unknown; q: string; t: string }>;
}

type ValuesHandler = (batch: WireValuesBatch) => void;
type AlarmsHandler = (evt: AlarmEvent) => void;
type CommandsHandler = (evt: CommandEvent) => void;
type NotificationsHandler = (evt: NotificationDto) => void;

/** QoS-1 wire envelope for alarms (mirrors HubAlarmBroadcaster.AlarmWireEnvelope).
 *  System.Text.Json uses camelCase for the C# record fields, so `sequence`
 *  and `event` land verbatim on the wire. */
interface AlarmWireEnvelope {
  sequence: number;
  event: AlarmEvent;
}
/** Sent when a leaf's schema metadata (thresholds, unit, min/max, etc.) was
 *  edited via PATCH /api/plants/{plantId}/leaves/{leafId}. Broadcast to the
 *  existing `values-{plantId}` group so every subscribed session sees the
 *  edit without a full schema refetch. */
export interface LeafSchemaUpdateEvent {
  leafId: string;
  plantId: string;
  displayName: string | null;
  unit: string | null;
  dimension: string | null;
  minValue: number | null;
  maxValue: number | null;
  decimals: number | null;
  warnLow: number | null;
  warnHigh: number | null;
  alarmLow: number | null;
  alarmHigh: number | null;
  readSensitivity: string;
  writeSensitivity: string;
  safetyMarker: boolean;
  forceAllowed: boolean;
  escalateToAlarm: boolean;
  metricKind: string;
}
type LeafSchemaUpdateHandler = (evt: LeafSchemaUpdateEvent) => void;

/** Field-client heartbeat during a running discovery. Broadcast on the
 *  plant's discovery-group (auto-joined via Subscribe). Used by the OT
 *  discovery-progress banner to reset a deadband — no absolute timeout. */
export interface DiscoveryProgressEvent {
  correlationId: string;
  deviceId: string;
  phase: string;
  stepDescription: string;
  completed: number | null;
  total: number | null;
  at: string;
}
type DiscoveryProgressHandler = (evt: DiscoveryProgressEvent) => void;

/** Schema-Change-Broadcast: der Server hat eine Schema-Änderung persistiert
 *  (Rename / Reparent / Delete / Optimize). Der Field-Client hat sie noch
 *  nicht bestätigt — die UI sollte den Sync-Badge auf "syncing" flippen. */
export interface SchemaChangedEvent {
  plantId: string;
  deviceId: string;
  schemaVersion: number;
  at: string;
}
/** Schema-Ack: der Field-Client hat den frischen OperationalMap installiert.
 *  UI flippt Badge zurück auf "synced" und toast'et. */
export interface SchemaAckedEvent {
  plantId: string;
  deviceId: string;
  schemaVersion: number;
  clientId: string;
  at: string;
}
type SchemaChangedHandler = (evt: SchemaChangedEvent) => void;
type SchemaAckedHandler = (evt: SchemaAckedEvent) => void;

const connectionStateSignal = signal<ConnectionState>('disconnected');

/** Live handlers for the `values` server event. We fan out ourselves so a
 *  second subscriber doesn't cause a second `.on('values', ...)` registration
 *  (SignalR would fire both, but keeping this a set is cleaner and lets us
 *  attach exactly once to the connection). */
const valuesHandlers = new Set<ValuesHandler>();

/** Live handlers for the `alarms` server event — same pattern as values.
 *  The hub auto-joins `alarms-{userId}` on connect (concept C.6). */
const alarmsHandlers = new Set<AlarmsHandler>();

/** QoS-1 client-side state: the highest sequence we have processed AND
 *  acked. Persisted only in-memory — on a full page reload the server
 *  will re-broadcast anything in its buffer that's still active, so we
 *  don't need durable storage. Used on reconnect to call
 *  ReplayAlarmsSince(lastAckedSequence). Starts at -1 so we replay
 *  everything the server has on first-ever connect. */
let lastAckedAlarmSequence = -1;

/** Fan-out one alarm envelope: apply to handlers, then ack the sequence
 *  server-side (fire-and-forget). Also called for each entry of a replay
 *  batch, so we re-ack after catching up too. */
function dispatchAlarm(envelope: AlarmWireEnvelope): void {
  for (const handler of alarmsHandlers) {
    try { handler(envelope.event); }
    catch (err) { console.error('[hubClient] alarms handler threw', err); }
  }
  if (envelope.sequence <= lastAckedAlarmSequence) return;
  lastAckedAlarmSequence = envelope.sequence;
  // Fire-and-forget: if the hub connection died right after we received
  // the event, .invoke() may reject. That's fine — on the next connect
  // ReplayAlarmsSince will resend anything the server still has buffered.
  if (connection && connection.state === HubConnectionState.Connected) {
    connection.invoke('AckAlarm', envelope.sequence).catch((err: unknown) => {
      console.debug('[hubClient] AckAlarm invocation failed', err);
    });
  }
}

/** Live handlers for the `commands` server event — same pattern as values.
 *  The hub auto-joins `commands-{userId}` on connect (concept C.5 step 6). */
const commandsHandlers = new Set<CommandsHandler>();

/** Live handlers for the `notifications` server event. The hub auto-joins
 *  `notifications-{userId}` on connect so mentioned users see the bell
 *  badge tick without any per-page subscribe. */
const notificationsHandlers = new Set<NotificationsHandler>();

/** Live handlers for the `schema.leaf.updated` server event — same pattern
 *  as values. Broadcast on the plant's values-group by the API when a
 *  privileged user edits a leaf's schema metadata. */
const leafSchemaHandlers = new Set<LeafSchemaUpdateHandler>();

/** Live handlers for the `discoveryProgress` server event. Same fan-out
 *  pattern as values/alarms/commands. */
const discoveryProgressHandlers = new Set<DiscoveryProgressHandler>();
const schemaChangedHandlers = new Set<SchemaChangedHandler>();
const schemaAckedHandlers = new Set<SchemaAckedHandler>();

let connection: HubConnection | null = null;
/** Promise held while start()/reconnect is in flight, so multiple concurrent
 *  callers await the same attempt instead of racing. */
let startPromise: Promise<void> | null = null;

function ensureConnection(): HubConnection {
  if (connection) return connection;

  connection = new HubConnectionBuilder()
    .withUrl('/hub', {
      // Cookie auth: the browser attaches the session cookie automatically
      // because the URL is same-origin. No accessTokenFactory.
      withCredentials: true,
    })
    // NOT the default array policy — that one gives up after 4 tries and
    // leaves the UI wedged until the user hits F5. See InfiniteRetryPolicy
    // at the top of the file.
    .withAutomaticReconnect(InfiniteRetryPolicy)
    .configureLogging(LogLevel.Warning)
    .build();

  connection.on('values', (batch: WireValuesBatch) => {
    for (const handler of valuesHandlers) {
      try {
        handler(batch);
      } catch (err) {
        // A misbehaving consumer must not tear down the whole hub loop.
        console.error('[hubClient] values handler threw', err);
      }
    }
  });

  // QoS-1 alarms channel — the server wraps every AlarmEvent in an
  // AlarmWireEnvelope { sequence, event } so the client can ack it and
  // replay any events it missed during a reconnect. Without unwrapping
  // (older code) every event looked "malformed" from the alarmsStore's
  // point of view (`evt.a` was actually `evt.event.a`), and the console
  // filled with dropped-malformed-event warnings.
  connection.on('alarms', (envelope: AlarmWireEnvelope) => {
    if (!envelope || envelope.event === undefined || typeof envelope.sequence !== 'number') {
      console.warn('[hubClient] alarm envelope malformed', envelope);
      return;
    }
    dispatchAlarm(envelope);
  });
  // Replay batch pushed by the server in response to ReplayAlarmsSince.
  connection.on('alarmReplay', (envelopes: AlarmWireEnvelope[]) => {
    if (!Array.isArray(envelopes)) return;
    for (const env of envelopes) {
      if (env && env.event !== undefined && typeof env.sequence === 'number') {
        dispatchAlarm(env);
      }
    }
  });

  connection.on('commands', (evt: CommandEvent) => {
    for (const handler of commandsHandlers) {
      try {
        handler(evt);
      } catch (err) {
        console.error('[hubClient] commands handler threw', err);
      }
    }
  });

  connection.on('notifications', (evt: NotificationDto) => {
    for (const handler of notificationsHandlers) {
      try {
        handler(evt);
      } catch (err) {
        console.error('[hubClient] notifications handler threw', err);
      }
    }
  });

  connection.on('schema.leaf.updated', (evt: LeafSchemaUpdateEvent) => {
    for (const handler of leafSchemaHandlers) {
      try {
        handler(evt);
      } catch (err) {
        console.error('[hubClient] leaf-schema handler threw', err);
      }
    }
  });

  connection.on('discoveryProgress', (evt: DiscoveryProgressEvent) => {
    for (const handler of discoveryProgressHandlers) {
      try {
        handler(evt);
      } catch (err) {
        console.error('[hubClient] discovery-progress handler threw', err);
      }
    }
  });

  connection.on('schemaChanged', (evt: SchemaChangedEvent) => {
    for (const handler of schemaChangedHandlers) {
      try { handler(evt); }
      catch (err) { console.error('[hubClient] schemaChanged handler threw', err); }
    }
  });
  connection.on('schemaAcked', (evt: SchemaAckedEvent) => {
    for (const handler of schemaAckedHandlers) {
      try { handler(evt); }
      catch (err) { console.error('[hubClient] schemaAcked handler threw', err); }
    }
  });

  connection.onreconnecting(() => {
    connectionStateSignal.value = 'reconnecting';
  });
  connection.onreconnected(() => {
    connectionStateSignal.value = 'connected';
    // QoS-1: ask the server to replay anything it broadcast while we were
    // offline. Fire-and-forget — if this rejects (edge case: connection
    // dies again immediately) the next reconnect will retry the ask.
    void requestAlarmReplay();
    // Fire our post-connect hooks so alarm/command stores can re-bootstrap.
    // Values re-sync themselves via the plant snapshot (signalStore).
    fireReconnectHandlers();
  });
  connection.onclose(() => {
    connectionStateSignal.value = 'disconnected';
    // Drop the promise so a fresh start() attempt is possible.
    startPromise = null;
    // withAutomaticReconnect only fires while the connection is in a
    // reconnecting-eligible state; a hard onclose (e.g. auth 401 during
    // handshake, or reconnect policy actually exhausted) bypasses it. We
    // schedule our own retry-until-connected as a safety net.
    scheduleReconnect();
  });

  return connection;
}

/** Idempotent connect. Returns a promise that resolves when the hub is
 *  connected (or rejects if the initial handshake failed). */
async function ensureStarted(): Promise<void> {
  const conn = ensureConnection();
  if (conn.state === HubConnectionState.Connected) return;
  if (startPromise) return startPromise;

  connectionStateSignal.value = 'connecting';
  startPromise = (async () => {
    try {
      await conn.start();
      connectionStateSignal.value = 'connected';
      // Same replay call as in onreconnected — a hard onclose followed by
      // a fresh start() also needs to catch up on missed events.
      void requestAlarmReplay();
      fireReconnectHandlers();
    } catch (err) {
      connectionStateSignal.value = 'disconnected';
      startPromise = null;
      // 401 during hub handshake = the cookie is gone. Without this the retry
      // loop below would just churn silently while the operator watched a
      // frozen dashboard. Trigger the same central redirect the API-client
      // uses (idempotent, so a parallel API 401 doesn't double-fire).
      if (isAuthError(err)) redirectToLogin();
      throw err;
    }
  })();
  return startPromise;
}

/** SignalR raises different error shapes depending on whether the handshake
 *  failed at HTTP negotiate (has a `statusCode`) or at the WebSocket upgrade
 *  (only carries a message like "Server returned handshake error … 401 …").
 *  We check both so cookie-expiry is caught regardless of transport. */
function isAuthError(err: unknown): boolean {
  if (!err) return false;
  const withStatus = err as { statusCode?: number };
  if (typeof withStatus.statusCode === 'number' && withStatus.statusCode === 401) return true;
  const msg = (err as { message?: string }).message ?? String(err);
  return /\b401\b|Unauthorized/i.test(msg);
}

/** Ask the hub to replay every alarm envelope with sequence > lastAcked.
 *  The server responds with an `alarmReplay` message (handled above). Silent
 *  if the connection isn't fully up — the retry loop will kick in on the
 *  next successful connect. */
async function requestAlarmReplay(): Promise<void> {
  if (!connection || connection.state !== HubConnectionState.Connected) return;
  try {
    await connection.invoke('ReplayAlarmsSince', lastAckedAlarmSequence);
  } catch (err: unknown) {
    console.debug('[hubClient] ReplayAlarmsSince failed', err);
  }
}

/** Safety-net reconnect after a hard onclose. withAutomaticReconnect covers
 *  network drops mid-session; this covers the cases where SignalR itself
 *  refuses to try further (auth 401 during handshake, exhausted policy, or
 *  server outright closed the socket). Retries with the same exp-backoff
 *  cap as {@link InfiniteRetryPolicy} until connected or replaced. */
let reconnectTimer: number | null = null;
let reconnectAttempts = 0;
function scheduleReconnect(): void {
  if (reconnectTimer !== null) return;
  const attempts = reconnectAttempts;
  const delay = attempts === 0
    ? 1_000
    : attempts === 1
      ? 5_000
      : attempts === 2
        ? 15_000
        : 30_000;
  reconnectTimer = window.setTimeout(async () => {
    reconnectTimer = null;
    reconnectAttempts++;
    try {
      await ensureStarted();
      reconnectAttempts = 0;
    } catch {
      // Failed; onclose will fire again and re-schedule.
      scheduleReconnect();
    }
  }, delay);
}

function fireReconnectHandlers(): void {
  for (const h of reconnectHandlers) {
    try { h(); } catch (err) { console.error('[hubClient] reconnect handler threw', err); }
  }
}

/** Called by bootstrappers that need to re-sync in-memory state after any
 *  full reconnect (fresh handshake OR successful auto-reconnect). Returns
 *  unsubscribe. */
export function onReconnected(handler: ReconnectHandler): () => void {
  reconnectHandlers.add(handler);
  return () => { reconnectHandlers.delete(handler); };
}

/** Reactive connection state. Read via `.value` inside a Preact component to
 *  re-render on change; read via `.peek()` where re-rendering isn't wanted
 *  (e.g. inside the freshness classifier). */
export function getConnectionState(): Signal<ConnectionState> {
  // Touch the builder so the state signal reflects reality even if nobody has
  // called subscribePlant yet. Cheap; the builder is lazy anyway.
  ensureConnection();
  return connectionStateSignal;
}

/** Register a handler for `values` server events. Returns the unsubscribe. */
export function onValues(handler: ValuesHandler): () => void {
  valuesHandlers.add(handler);
  return () => {
    valuesHandlers.delete(handler);
  };
}

/** Register a handler for `alarms` server events. Returns the unsubscribe.
 *  The hub auto-joins the caller into `alarms-{userId}` on connect, so once
 *  the connection is up the handler will fire on every lifecycle transition
 *  (raised / cleared / acknowledged) that the server broadcasts. */
export function onAlarms(handler: AlarmsHandler): () => void {
  // Touch the builder so the hub is created (and will be started when the
  // first consumer needs it). The alarms store calls ensureStarted itself,
  // but keep the pattern parallel to onValues for symmetry.
  ensureConnection();
  alarmsHandlers.add(handler);
  return () => {
    alarmsHandlers.delete(handler);
  };
}

/** Register a handler for `commands` server events. Returns the unsubscribe.
 *  The hub auto-joins the caller into `commands-{userId}` on connect
 *  (concept C.5 step 6), so once the connection is up the handler will fire
 *  on every lifecycle transition (ack / executed / failed / timeout) the
 *  server broadcasts for this user's commands. */
export function onCommands(handler: CommandsHandler): () => void {
  ensureConnection();
  commandsHandlers.add(handler);
  return () => {
    commandsHandlers.delete(handler);
  };
}

/** Register a handler for `notifications` server events. Returns the
 *  unsubscribe. The hub auto-joins the caller into `notifications-{userId}`
 *  on connect, so once the connection is up the handler will fire on every
 *  MentionNotification the server broadcasts for this user. */
export function onNotifications(handler: NotificationsHandler): () => void {
  ensureConnection();
  notificationsHandlers.add(handler);
  return () => {
    notificationsHandlers.delete(handler);
  };
}

/** Register a handler for `discoveryProgress` server events. The event
 *  arrives on the plant's discovery-group, so any session that has called
 *  `subscribePlant()` will receive ticks while a field-client discovery is
 *  running against that plant. Returns unsubscribe. */
export function onDiscoveryProgress(handler: DiscoveryProgressHandler): () => void {
  ensureConnection();
  discoveryProgressHandlers.add(handler);
  return () => {
    discoveryProgressHandlers.delete(handler);
  };
}

/** Schema-Change: server persisted a rename/reparent/delete/optimize. */
export function onSchemaChanged(handler: SchemaChangedHandler): () => void {
  ensureConnection();
  schemaChangedHandlers.add(handler);
  return () => { schemaChangedHandlers.delete(handler); };
}
/** Schema-Ack: field-client installed the new operational map. */
export function onSchemaAcked(handler: SchemaAckedHandler): () => void {
  ensureConnection();
  schemaAckedHandlers.add(handler);
  return () => { schemaAckedHandlers.delete(handler); };
}

/** Register a handler for `schema.leaf.updated` server events. The event
 *  arrives on the plant's values-group, so any session that has subscribed
 *  to the plant via `subscribePlant()` will receive it. Returns unsubscribe. */
export function onLeafSchemaUpdated(handler: LeafSchemaUpdateHandler): () => void {
  ensureConnection();
  leafSchemaHandlers.add(handler);
  return () => {
    leafSchemaHandlers.delete(handler);
  };
}

/** Explicitly start the hub connection. Alarms are auto-joined server-side on
 *  connect, so the store needs a way to nudge the hub open even without a
 *  plant subscription. Idempotent. */
export function ensureHubStarted(): Promise<void> {
  return ensureStarted();
}

/** Ask the hub to add us to the `values-{plantId}` group.
 *
 *  The server enforces Tor-2 authorization here (concept E.3): if the caller
 *  isn't allowed to see the plant, `Subscribe` throws and the signal store
 *  will surface it. */
export async function subscribePlant(plantId: string): Promise<void> {
  await ensureStarted();
  const conn = ensureConnection();
  await conn.invoke('Subscribe', plantId);
}

/** Was `StartLive` / `ExtendLive` zurueckgeben (Server: LiveSessionTicket).
 *  System.Text.Json schreibt camelCase, die Feldnamen landen also so auf dem
 *  Draht.
 *
 *  `ttlSeconds` und `heartbeatSeconds` kommen bewusst vom Server: stuenden
 *  die Zahlen hier fest und dort in der Konfiguration, waere eine Aenderung
 *  am Server ein Live-Modus, der im Sekundentakt an- und ausgeht. */
export interface LiveSessionTicket {
  sessionId: string;
  plantId: string;
  ttlSeconds: number;
  heartbeatSeconds: number;
  /** Hartes Ende dieser Sitzung (ISO). Kein Heartbeat verschiebt es. */
  hardExpiresAt: string;
}

/** Live-Modus fuer eine Anlage anfordern. Der Server legt die Sitzung an und
 *  schickt dem Field-Client sein erstes `StartLive`.
 *
 *  Wirft, wenn der Aufrufer die Anlage nicht sehen darf (dasselbe Tor 2 wie
 *  `Subscribe`). */
export async function startLive(plantId: string): Promise<LiveSessionTicket> {
  await ensureStarted();
  const conn = ensureConnection();
  return (await conn.invoke('StartLive', plantId)) as LiveSessionTicket;
}

/** Heartbeat. `null` heisst: diese Sitzung ist zu Ende (abgelaufen,
 *  geschlossen, oder an ihrer harten Grenze).
 *
 *  Bewusst kein Fehler: eine harte Grenze, die als Exception in der Konsole
 *  des Betrachters landet, sieht wie ein Defekt aus. Der Aufrufer entscheidet
 *  anhand von `hardExpiresAt`, ob eine neue Sitzung angebracht ist. */
export async function extendLive(sessionId: string): Promise<LiveSessionTicket | null> {
  const conn = ensureConnection();
  if (conn.state !== HubConnectionState.Connected) return null;
  return (await conn.invoke('ExtendLive', sessionId)) as LiveSessionTicket | null;
}

/** Live-Modus vorzeitig beenden. Beschleunigung, nicht Bedingung: bleibt der
 *  Aufruf aus, laeuft die Sitzung nach ihrer TTL ab und der Field-Client
 *  faellt von selbst zurueck. Deshalb ist ein Fehler hier nur eine
 *  Logzeile. */
export async function stopLive(sessionId: string): Promise<void> {
  const conn = ensureConnection();
  if (conn.state !== HubConnectionState.Connected) return;
  try {
    await conn.invoke('StopLive', sessionId);
  } catch (err) {
    console.warn('[hubClient] StopLive fehlgeschlagen (die TTL beendet Live ohnehin)', err);
  }
}

/** Ask the hub to remove us from the `values-{plantId}` group. Safe to call
 *  when disconnected — we swallow that case, since the server has already
 *  dropped the group. */
export async function unsubscribePlant(plantId: string): Promise<void> {
  const conn = ensureConnection();
  if (conn.state !== HubConnectionState.Connected) return;
  try {
    await conn.invoke('Unsubscribe', plantId);
  } catch (err) {
    // Best-effort: on transient failure we drop the request. The server-side
    // group membership will be cleaned up when the connection closes.
    console.warn('[hubClient] Unsubscribe failed (non-fatal)', err);
  }
}
